import { parseKpiSettings, type KpiSettingsMap } from '../audit/budgets';
import { getListItem, getListItems, odataString } from '../graph/lists';
import { LISTS, READ_CACHE_MS } from './listNames';
import type { TemplateDefinition } from '../audit/template';
import type { TemplateSettings } from '../audit/validateTemplate';
import { requirementSettingsOf as sharedRequirementSettingsOf, requirementsOf, type Requirement } from '../audit/requirements';
import { parseAssignment, type TemplateAssignment } from '../audit/assignment';

/** A published, conductable template loaded from AuditTemplates. */
export interface PublishedTemplate {
  /** SharePoint item id — referenced by Audits.TemplateId. */
  id: number;
  name: string;
  auditType: string;
  version: number;
  /** The DM requirement's window (0 when the template has none). */
  frequencyDays: number;
  dueSoonThresholdPct: number;
  /** Who must do it, and how often — one compliance clock each (src/audit/requirements.ts). */
  requirements: Requirement[];
  /** Which stores it applies to (src/audit/assignment.ts). */
  assignment: TemplateAssignment;
  /** How each KPI compares with its budget (B8, src/audit/budgets.ts) — the same on every version. */
  kpiSettings?: KpiSettingsMap;
  settings: TemplateSettings;
  definition: TemplateDefinition;
}

export interface TemplateFields {
  Title: string;
  AuditType: string;
  TemplateVersion: number;
  TemplateStatus: string;
  FrequencyDays: number;
  DueSoonThresholdPct: number;
  /**
   * Yes = an auditor-only template. Inverted on purpose: SharePoint's own
   * form shows a blank Yes/No as "No" and saves it, so the blank/No state
   * must be the SAFE one — the DM clock stays on (QA review 2026-10-18).
   */
  DmNotRequired: boolean | null;
  /** Blank = no Auditor requirement. */
  AuditorFrequencyDays: number | null;
  /** Blank = no Store team requirement (owner rulings 2026-10-19). */
  StoreFrequencyDays: number | null;
  /** DM window for Long-distance stores; blank = FrequencyDays (owner rulings 2026-10-20). */
  LongDistanceFrequencyDays: number | null;
  /** Auditor window for Long-distance stores; blank = AuditorFrequencyDays. */
  AuditorLongDistanceFrequencyDays: number | null;
  /**
   * Yes = Flow 1 sends no "due soon" email for this template (overdue and
   * escalation mail still go). Inverted like DmNotRequired: blank = send.
   */
  DueSoonRemindersOff: boolean | null;
  /** JSON template assignment (src/audit/assignment.ts); blank = all stores. Same on every row of the family. */
  AssignedTo: string;
  /** JSON KPI settings (src/audit/budgets.ts, B8); blank = defaults. Same on every row of the family. */
  KpiSettings: string;
  ScoringMode: string;
  CriticalBehavior: string;
  CriticalCap: number;
  Definition: string;
  PublishedBy: string;
  PublishedAt: string | null;
  /** Who last saved the row and when — set by the server from the token on every write (2026-10-29). */
  EditedBy?: string | null;
  EditedAt?: string | null;
}

export function toSettings(f: Partial<TemplateFields>): TemplateSettings {
  const mode =
    f.ScoringMode === 'none' ||
    f.ScoringMode === 'simple' ||
    f.ScoringMode === 'weighted' ||
    f.ScoringMode === 'weighted_critical'
      ? f.ScoringMode
      : 'weighted';
  if (mode !== 'weighted_critical') return { scoringMode: mode };
  return {
    scoringMode: mode,
    ...(f.CriticalBehavior === 'auto_fail' || f.CriticalBehavior === 'cap'
      ? { criticalBehavior: f.CriticalBehavior }
      : {}),
    ...(typeof f.CriticalCap === 'number' ? { criticalCap: f.CriticalCap } : {}),
  };
}

/** The row's responsible-party columns, defaults applied (shared with the server). */
export function requirementSettingsOf(f: Partial<TemplateFields>) {
  return sharedRequirementSettingsOf(f as Record<string, unknown>);
}

function parseRow(
  id: string,
  f: Partial<TemplateFields>,
): PublishedTemplate | null {
  if (!f.Definition) return null;
  let definition: TemplateDefinition;
  try {
    definition = JSON.parse(f.Definition) as TemplateDefinition;
  } catch {
    console.error(`AuditTemplates item ${id}: Definition is not valid JSON`);
    return null;
  }
  // Same "readable" rule as Admin → Templates (toTemplateRow): no sections
  // array = unreadable, so it is never offered as the live version.
  if (!definition || typeof definition !== 'object' || !Array.isArray(definition.sections)) {
    console.error(`AuditTemplates item ${id}: Definition has no sections array`);
    return null;
  }
  return {
    id: Number(id),
    name: f.Title ?? definition.name,
    auditType: f.AuditType ?? definition.auditType,
    version: f.TemplateVersion ?? 1,
    frequencyDays: f.FrequencyDays ?? 0,
    dueSoonThresholdPct: f.DueSoonThresholdPct ?? 80,
    requirements: requirementsOf(requirementSettingsOf(f)),
    assignment: parseAssignment(f.AssignedTo),
    kpiSettings: parseKpiSettings(f.KpiSettings),
    settings: toSettings(f),
    definition,
  };
}

/**
 * One live version per template (owner decision 2026-10-16): rows sharing an
 * AuditType are versions of the same template, and only the highest
 * Published version is offered. Normally there is exactly one; two appear
 * only for the moment a publish is in flight (or if one stopped half-way —
 * Admin → Templates shows a "Finish publishing" banner then).
 */
export function livePerFamily(rows: PublishedTemplate[]): PublishedTemplate[] {
  const best = new Map<string, PublishedTemplate>();
  for (const t of rows) {
    const key = t.auditType.trim().toLowerCase();
    const seen = best.get(key);
    if (!seen || t.version > seen.version || (t.version === seen.version && t.id > seen.id)) {
      best.set(key, t);
    }
  }
  return [...best.values()];
}

/** The live Published version of each template (TemplateStatus is indexed). */
export async function getPublishedTemplates(): Promise<PublishedTemplate[]> {
  const items = await getListItems<TemplateFields>(LISTS.auditTemplates, {
    filter: `fields/TemplateStatus eq ${odataString('Published')}`,
    cacheMs: READ_CACHE_MS,
  });
  return livePerFamily(
    items
      .map((i) => parseRow(i.id, i.fields))
      .filter((t): t is PublishedTemplate => t !== null),
  ).sort((a, b) => a.name.localeCompare(b.name));
}

/** One template row by item id (for server-resume); any status. */
export async function getTemplateById(
  templateId: number,
): Promise<PublishedTemplate | null> {
  const item = await getListItem<TemplateFields>(
    LISTS.auditTemplates,
    templateId,
  );
  return item ? parseRow(item.id, item.fields) : null;
}
